import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import Icon from './Icon.jsx'
import Popover from './Popover.jsx'
import GlyphTile from './GlyphTile.jsx'
import LucideIcon, { LUCIDE_NAMES, lucideGlyph } from './LucideIcon.jsx'
import { LIST_COLORS } from '../lib/lists.js'

/**
 * What a list looks like — its colour and its glyph — as ONE control.
 *
 * These were three things beside each other: a colour input, an icon dropdown naming the
 * icon in monospace, and a tile previewing the pair. Three controls for one decision, and
 * the only one that showed the actual answer was the one you could not click. So the
 * PREVIEW is now the trigger: the tile you are choosing is the button you press, and the
 * panel under it holds both halves of the choice.
 *
 * The panel does not close on a pick. Colour and glyph are two choices in one visit —
 * closing on the first would mean opening it again for the second — so it closes the way
 * MultiSelectDropdown's does, on a click away or Escape (see Popover.jsx).
 *
 * The icon's NAME appears only as a tooltip. 'phone-call' is how the library keys its
 * glyphs, not something a person setting up an on-call list has any use for reading.
 */

// The likely ones, while nothing has been typed. A list is nearly always one of these
// handful of ideas, and a search box in front of eighteen hundred glyphs is a worse first
// move than a row you can simply point at.
const SUGGESTED = [
  'list-tree', 'list-checks', 'clipboard-list', 'calendar-days', 'users', 'user-check',
  'phone', 'phone-call', 'truck', 'hard-hat', 'wrench', 'shield-check', 'flame', 'siren',
  'graduation-cap', 'stethoscope', 'key-round', 'map-pin', 'bell', 'star', 'flag', 'coffee',
  'moon', 'sun',
].filter((n) => LUCIDE_NAMES.includes(n))

// A screenful. Fetching a thousand icon modules for a panel somebody is about to type three
// letters into is the cost this avoids; the search narrows to well under this.
const PAGE = 96

export default function LookPicker({ color, icon, onChange, disabled = false }) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const anchorRef = useRef(null)
  const close = useCallback(() => setOpen(false), [])

  useEffect(() => { if (!open) setQuery('') }, [open])

  // Typed with spaces, matched with hyphens — "hard hat" should find hard-hat.
  const needle = query.trim().toLowerCase().replace(/\s+/g, '-')
  const matches = useMemo(() => {
    if (!needle) return SUGGESTED
    const starts = []
    const contains = []
    for (const n of LUCIDE_NAMES) {
      if (n.startsWith(needle)) starts.push(n)
      else if (n.includes(needle)) contains.push(n)
    }
    return [...starts, ...contains]
  }, [needle])
  const shown = matches.slice(0, PAGE)
  const more = matches.length - shown.length

  const custom = !LIST_COLORS.includes(color)

  return (
    <div className="filter-dropdown look-picker" ref={anchorRef}>
      {/* The tile IS the preview: this is exactly how the list will be drawn in the
          sidebar, on its board header and on the /lists page. */}
      <button
        type="button"
        className="look-trigger"
        disabled={disabled}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label="Colour and icon"
        title="Colour and icon"
        onClick={() => setOpen((o) => !o)}
      >
        <GlyphTile icon={lucideGlyph(icon)} tint={color} size={30} />
        <Icon name="down" size={12} strokeWidth={2.2} />
      </button>

      <Popover
        open={open}
        anchorRef={anchorRef}
        close={close}
        className="filter-panel look-panel"
        label="Colour and icon"
        align="left"
        placeKey={`${needle}:${shown.length}`}
        autoFocus
      >
        <div className="look-colors">
          {LIST_COLORS.map((c) => (
            <button
              key={c}
              type="button"
              className={`look-color ${c === color ? 'on' : ''}`}
              style={{ background: c }}
              aria-label={`Colour ${c}`}
              aria-pressed={c === color}
              onClick={() => onChange({ color: c, icon })}
            />
          ))}
          {/* Anything not on the row. Native, so it opens the platform's own colour dialog
              rather than this app inventing a second one. */}
          <input
            type="color"
            className={`team-swatch look-color-custom ${custom ? 'on' : ''}`}
            value={color}
            title="Another colour"
            aria-label="Another colour"
            onChange={(e) => onChange({ color: e.target.value, icon })}
          />
        </div>

        <div className="look-sep" />

        <div className="filter-panel-search">
          <Icon name="search" size={13} />
          <input
            type="text"
            value={query}
            placeholder="Search icons…"
            aria-label="Search icons"
            onChange={(e) => setQuery(e.target.value)}
            // Enter takes the best match, so "hard hat, Enter" is the whole gesture.
            onKeyDown={(e) => {
              if (e.key === 'Enter' && shown[0]) {
                e.preventDefault()
                onChange({ color, icon: shown[0] })
              }
            }}
          />
        </div>

        <div className="icon-picker-grid" role="listbox" aria-label="Icons">
          {shown.map((name) => (
            <button
              key={name}
              type="button"
              role="option"
              aria-selected={name === icon}
              className={`icon-picker-cell ${name === icon ? 'on' : ''}`}
              // The name is a tooltip and a label for a screen reader, and nothing a sighted
              // person has to read to make this choice.
              title={name}
              aria-label={name}
              onClick={() => onChange({ color, icon: name })}
            >
              <LucideIcon name={name} size={18} />
            </button>
          ))}
          {shown.length === 0 && <div className="ctx-label">Nothing matches</div>}
        </div>
        {more > 0 && (
          <div className="icon-picker-more">{more} more — keep typing to narrow it down</div>
        )}
      </Popover>
    </div>
  )
}
